/**
 * @alias mentions in comments. Mirrors the backend parser (investigation_mentions.py): an alias
 * starts with a letter, `@` must not follow a word character, `.`, `/` or `@` (e-mails, URLs), and
 * the alias must not run on into `.word`. Only aliases the backend accepted (InvestigationEvent
 * .mentions) or, while composing, Lusca Search users are highlighted; anything else stays text.
 */
/** An @alias in text; shared with the editor's chips (mentionTokens) so they never drift. */
export const MENTION_RE =
  /(?<![\w@./])@([a-z][a-z0-9_-]{0,31})(?![\w@-]|\.\w)/gi;

export type MentionPart = string | { alias: string; text: string };

/** Split prose into plain strings and the mentions of `known` aliases (case-insensitive). */
export function splitMentions(
  text: string,
  known: ReadonlySet<string>
): MentionPart[] {
  if (!text.includes("@") || known.size === 0) return [text];
  const parts: MentionPart[] = [];
  let last = 0;
  Array.from(text.matchAll(MENTION_RE))
    .filter((match) => known.has(match[1].toLowerCase()))
    .forEach((match) => {
      const at = match.index ?? 0;
      if (at > last) parts.push(text.slice(last, at));
      parts.push({ alias: match[1].toLowerCase(), text: match[0] });
      last = at + match[0].length;
    });
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

/** Aliases written as @alias in `text`, lower-cased, de-duplicated, in order. */
export function extractMentions(text: string): string[] {
  const aliases = Array.from(text.matchAll(MENTION_RE), (m) =>
    m[1].toLowerCase()
  );
  return aliases.filter((alias, i) => aliases.indexOf(alias) === i);
}
